/**
 * PinReset Component
 * Password/PIN reset request form
 */

import React, { useCallback, useState } from 'react';

import { POButton, POButtonType, POLinkButton } from '@prizeout/prizeout-ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { Alert } from '../../components/alert/alert';
import { FormInput } from '../../components/form-input/form-input';
import { useAppDispatch, useGoToRoute } from '../../hooks';
import { requestPinReset } from '../../store/slices';
import { routes } from '../../utils/routes';
import { validationRules } from '../../utils/validation';

import './pin-reset.scss';

interface PinResetInputs {
    accessCode: string;
}

type ResetStatus = 'idle' | 'loading' | 'success' | 'error';

export const PinReset: React.FC = () => {
    const dispatch = useAppDispatch();
    const { goToRoute } = useGoToRoute();
    const location = useLocation();
    const [status, setStatus] = useState<ResetStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const passedAccessCode = (location.state as { accessCode?: string } | null)?.accessCode || '';

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<PinResetInputs>({
        mode: 'onBlur',
        defaultValues: {
            accessCode: passedAccessCode,
        },
    });

    const onSubmit: SubmitHandler<PinResetInputs> = useCallback(
        async (data) => {
            setStatus('loading');
            setErrorMessage('');

            const accessCode = data.accessCode.trim();

            try {
                const result = await dispatch(requestPinReset(accessCode));

                // Check if the action was rejected
                if (requestPinReset.rejected.match(result)) {
                    throw result.payload || 'Request failed';
                }

                setStatus('success');
            } catch (error) {
                setStatus('error');
                setErrorMessage(typeof error === 'string' ? error : 'Failed to send reset request');
            }
        },
        [dispatch],
    );

    const handleBackToLogin = useCallback(() => {
        goToRoute(routes.WELCOME);
    }, [goToRoute]);

    if (status === 'success') {
        return (
            <div className="pin-reset">
                <div className="pin-reset__content">
                    <div className="pin-reset__success">
                        <div className="pin-reset__icon pin-reset__icon--success">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <h2 className="pin-reset__title">Check Your Email</h2>
                        <p className="pin-reset__message">
                            We&apos;ve sent password reset instructions to the email associated with your access code.
                        </p>
                        <POButton label="Back to Login" onClick={handleBackToLogin} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pin-reset">
            <div className="pin-reset__content">
                <div className="pin-reset__header">
                    <h1 className="pin-reset__title">Reset Your Passcode</h1>
                    <p className="pin-reset__subtitle">
                        Enter your access code and we&apos;ll send you instructions to reset your passcode.
                    </p>
                </div>

                {status === 'error' && errorMessage && (
                    <div className="pin-reset__alerts">
                        <Alert type="error" message={errorMessage} />
                    </div>
                )}

                <form
                    id="pin-reset-form"
                    className="pin-reset__form"
                    onSubmit={handleSubmit(onSubmit)}
                    autoComplete="off"
                >
                    <FormInput
                        id="accessCode"
                        label="Access Code"
                        type="text"
                        placeholder="Enter your access code"
                        error={!!errors.accessCode}
                        errorMessage={errors.accessCode?.message}
                        {...register('accessCode', validationRules.accessToken)}
                    />
                </form>

                <div className="pin-reset__actions">
                    <POButton
                        label="Send Reset Instructions"
                        isLoading={status === 'loading'}
                        formId="pin-reset-form"
                        type={POButtonType.SUBMIT}
                    />

                    <POLinkButton label="Back to Login" onClick={handleBackToLogin} />
                </div>
            </div>
        </div>
    );
};

export default PinReset;
